/* eslint-disable react-hooks/rules-of-hooks */
/* eslint-disable @typescript-eslint/restrict-plus-operands */
/* eslint-disable @typescript-eslint/restrict-template-expressions */
import { Button, DrawerProps, Table, Collapse, Tooltip } from 'antd';
import { antdDrawer, useModal, create } from '@ebay/nice-modal-react';
import './TableDetailsDrawer.less';
import { CustomDrawer } from '@isocel/front-common';
import TextArea from 'antd/lib/input/TextArea';
import React, { useMemo, useState } from 'react';
import moment from 'moment';

import { PackagingFlowDirection, PackagingVoucher, PackagingVoucherStatus } from '../../../queries/api/types';
import {
    downloadFile,
    downloadPackagingVoucher,
    getAllPackagingFlow,
    getPackagingFlowByPackagingVoucher,
    updatePackageItemStatus,
} from '../../../queries/api/packaging-flow';
import { printPDF } from '../../../utils/utils';
import { PackagingFlowTypeMap } from '../../../constants';

const { Panel } = Collapse;

type Props = DrawerProps & Record<string, any>;
const TableDetailsFormDrawer = create<Props>(({ data, storeId, refreshDashboard, ...props }) => {
    const modal = useModal();
    const [packagesThirdFlow, setPackagesThirdFlow] = useState<any[]>([]);
    const [packages, setPackages] = useState(data.packages);

    // Keep packages state in sync with data.packages
    React.useEffect(() => {
        setPackages(data.packages);
    }, [data.packages]);

    const formatDate = (dateString: string) => {
        return moment(dateString).format('MM/DD/yyyy - HH:mm');
    };

    const acceptPackage = async (packageItemId: string) => {
        try {
            const packageToUpdate = packages.find((pkg: any) => pkg._id === packageItemId);
            if (!packageToUpdate) return;

            await updatePackageItemStatus(data.key, packageItemId, true);
            setPackages((prevPackages: any[]) =>
                prevPackages.map((pkg: any) => {
                    if (pkg._id === packageItemId) {
                        return {
                            ...pkg,
                            organizationRefused: false,
                            statusDifferenceReported: 'Accepté',
                        };
                    }
                    return pkg;
                })
            );
        } catch (error) {
            console.error('Error accepting package:', error);
        }
    };

    const refusePackage = async (packageItemId: string) => {
        try {
            const packageToUpdate = packages.find((pkg: any) => pkg._id === packageItemId);
            if (!packageToUpdate) return;

            await updatePackageItemStatus(data.key, packageItemId, false);
            setPackages((prevPackages: any[]) =>
                prevPackages.map((pkg: any) => {
                    if (pkg._id === packageItemId) {
                        return {
                            ...pkg,
                            organizationRefused: true,
                            statusDifferenceReported: 'Refusé',
                        };
                    }
                    return pkg;
                })
            );
        } catch (error) {
            console.error('Error refusing package:', error);
        }
    };
    const getDeclaredQuantityColumn = (title: string, dataIndex: string, key: string) => ({
        title,
        dataIndex,
        key,
        render: (_: any, pkg: any) => {
            return <div>{pkg.shippedQuantity}</div>;
        },
    });

    const delay = async (ms: number) => await new Promise((resolve) => setTimeout(resolve, ms));

    const downloadFiles = async () => {
        try {
            for (const attachment of data.attachments) {
                const fileId = attachment.remoteFile?._id;
                const fileName = attachment.remoteFile?.originalName || attachment.remoteFile?.filename || 'file'; // Get the file name

                if (!fileId) {
                    console.error('File ID not found for attachment:', attachment);
                    continue;
                }
                const response = await downloadFile(fileId);
                const contentType = response.headers['content-type'];

                const blob = new Blob([response.data], { type: contentType });

                const url = window.URL.createObjectURL(blob);

                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', fileName);
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                await delay(500);
            }
        } catch (error) {
            console.error('Error downloading files:', error);
        }
    };
    // Memoize the API call result to avoid multiple calls
    const [, setThirdFlowLoading] = useState(false);
    const [, setThirdFlowError] = useState<Error | null>(null);

    useMemo(() => {
        let cancelled = false;
        if (!data.transportVoucher) return;

        setThirdFlowLoading(true);
        setThirdFlowError(null);

        getAllPackagingFlow({
            transportVoucher: data.transportVoucher,
            flowType: PackagingFlowDirection.ShippedToOrganization,
        })
            .then((response) => {
                if (!cancelled) {
                    setPackagesThirdFlow(response?.items[0]?.packages || []);
                }
            })
            .catch((err) => {
                if (!cancelled) setThirdFlowError(err);
            })
            .finally(() => {
                if (!cancelled) setThirdFlowLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [data.transportVoucher]);

    const getThirFlow = (title: string, dataIndex: string, key: string) => ({
        title,
        dataIndex,
        key,
        render: (_: any, pkg: any) => {
            // Find the matching package in packagesThirdFlow by _id or another unique property
            const thirdFlowPkg = packagesThirdFlow.find((p: any) => p._id === pkg._id);
            return thirdFlowPkg ? (
                <div>
                    {thirdFlowPkg.goodCount} bon état {thirdFlowPkg.badCount} cassé
                </div>
            ) : null;
        },
    });

    const downloadDocuments = async () => {
        try {
            if (data.attachments.length > 0) {
                await downloadFiles();
            }
            const usedVoucherIds = Array.isArray(data.packagingVoucherUsed)
                ? data.packagingVoucherUsed.map((voucher: any) => voucher._id).filter(Boolean)
                : [];
            const bonVoucherIds = new Set<string>(
                data.packagingVouchers.map((voucher: PackagingVoucher) => voucher._id)
            );

            const allVoucherIds = new Set([...usedVoucherIds, ...bonVoucherIds]);

            for (const voucherId of allVoucherIds) {
                try {
                    const response = await downloadPackagingVoucher(voucherId);
                    const VoucherResponse = await getPackagingFlowByPackagingVoucher([voucherId]);
                    const voucherNumber =
                        VoucherResponse[0]?.packagingVouchers.find(
                            (voucher: PackagingVoucher) => voucher._id === voucherId
                        )?.voucherNumber || voucherId;
                    // Create a URL for the PDF blob
                    const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));

                    // Create a link element and trigger the download
                    const link = document.createElement('a');
                    link.href = url;
                    link.setAttribute('download', `Bon_emballage_${voucherNumber}.pdf`);
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);

                    // Add a delay between requests
                    await delay(500); // 500ms delay
                } catch (error) {
                    console.error(`Error downloading voucher ID ${voucherId}:`, error);
                }
            }
        } catch (error) {
            console.error('Error downloading documents:', error);
        }
    };

    const handlePrintVoucher = async (voucherId: string) => {
        if (!voucherId) {
            return;
        }
        const response = await downloadPackagingVoucher(voucherId);
        await printPDF(
            new Blob([response.data], { type: 'application/pdf' }),
            () => () => console.error('Error printing voucher:')
        );
    };
    const quantityRecievedInStore = (title: string, dataIndex: string, key: string) => ({
        title,
        dataIndex,
        key,
        render: (_: any, pkg: any) => {
            return <div>{pkg.goodCount + pkg.badCount}</div>;
        },
    });

    const getQualityColumn = (title: string, dataIndex: string, key: string) => ({
        title,
        dataIndex,
        key,
        render: (_: any, pkg: any) => {
            return (
                <div>
                    {pkg.goodCount} bon état {pkg.badCount} cassé
                </div>
            );
        },
    });
    const getQualityColumnDebtDeduction = (title: string, dataIndex: string, key: string) => ({
        title,
        dataIndex,
        key,
        render: (_: any, pkg: any) => {
            if (!pkg.rental) {
                return (
                    <div>
                        {pkg.goodCount} bon état {pkg.badCount} cassé
                    </div>
                );
            }
            return null;
        },
    });
    const getTypologyColumnDebtDeduction = (title: string, dataIndex: string, key: string) => ({
        title,
        dataIndex,
        key,
        render: (_: any, pkg: any) => {
            if (!pkg.rental) {
                return <div key={pkg._id}>{pkg.model === pkg.type ? pkg.type : `${pkg.type} ${pkg.model}`}</div>;
            }
            return null;
        },
    });
    const getDifferenceColumn = (title: string, dataIndex: string, key: string) => ({
        title,
        dataIndex,
        key,
        render: (_: any, pkg: any) => {
            const totalQuality = pkg.goodCount + pkg.badCount;
            const difference = totalQuality - pkg.shippedQuantity;
            return <div>{difference}</div>;
        },
    });
    const getPackagesToBeReturned = (title: string, dataIndex: string, key: string) => ({
        title,
        dataIndex,
        key,
        render: (_: any, pkg: any) => {
            return <div>{pkg.toBeReturned}</div>;
        },
    });

    const rowSpanMap = useMemo(() => {
        const map: Record<string, number> = {};
        data.packages.forEach((pkg: any) => {
            const voucherId = pkg.packagingVoucherId;
            if (!map[voucherId]) {
                map[voucherId] = data.packages.reduce((acc: number, p: any) => {
                    return acc + (p.packagingVoucherId === voucherId ? 1 : 0);
                }, 0);
            }
        });
        return map;
    }, [data.packages]);

    const getColumns = (flowType: PackagingFlowDirection, differenceReported: boolean, storeId: string | undefined) => {
        const baseColumns = [
            {
                title: 'Typologie',
                dataIndex: 'typology',
                key: 'typology',
                render: (_: any, pkg: any) => (
                    <>
                        <div>{pkg.model === pkg.type ? pkg.type : `${pkg.type} ${pkg.model}`}</div>
                    </>
                ),
            },
        ];

        const getCommonColumns = () => [
            ...baseColumns,
            getQualityColumn('Qualité transférée', 'QualitéTransférée', 'QualitéTransférée'),
        ];

        let columns = [];

        switch (flowType) {
            case PackagingFlowDirection.ShippedToStore:
                columns = [
                    ...baseColumns,
                    getDeclaredQuantityColumn(
                        'Quantité déclarée par la centrale',
                        'declaredQuantityByCentral',
                        'declaredQuantityByCentral'
                    ),
                ];
                break;
            case PackagingFlowDirection.RecivedInStore:
                columns = [
                    ...baseColumns,
                    getDeclaredQuantityColumn(
                        'Quantité déclarée par la centrale',
                        'declaredQuantityByCentral',
                        'declaredQuantityByCentral'
                    ),
                    getDifferenceColumn(
                        'Différence reçue en magasin',
                        'differenceReceivedInStore',
                        'differenceReceivedInStore'
                    ),
                    getQualityColumn('Qualité reçue en magasin', 'qualityReceivedInStore', 'qualityReceivedInStore'),
                ];
                break;
            case PackagingFlowDirection.AutomaticReception:
                columns = [
                    ...baseColumns,
                    getDeclaredQuantityColumn(
                        'Quantité déclarée par la centrale',
                        'declaredQuantityByCentral',
                        'declaredQuantityByCentral'
                    ),
                    getDifferenceColumn(
                        'Différence reçue en magasin',
                        'differenceReceivedInStore',
                        'differenceReceivedInStore'
                    ),
                    getQualityColumn('Qualité reçue en magasin', 'qualityReceivedInStore', 'qualityReceivedInStore'),
                ];
                break;
            case PackagingFlowDirection.ShippedToOrganization:
                columns = [
                    ...baseColumns,
                    quantityRecievedInStore(
                        'Quantité déclarée par le magasin',
                        'declaredQuantityByStore',
                        'declaredQuantityByStore'
                    ),
                    getQualityColumn('Qualité envoyée par le magasin', 'qualitySentByStore', 'qualitySentByStore'),
                ];
                break;
            case PackagingFlowDirection.RecivedInOrganization:
                if (data.originId) {
                    return [
                        ...baseColumns,
                        getDeclaredQuantityColumn(
                            'Quantité déclarée par le magasin',
                            'declaredQuantityByStore',
                            'declaredQuantityByStore'
                        ),
                        getThirFlow('Qualité envoyée par le magasin', 'qualitySentByStore', 'qualitySentByStore'),
                        getDifferenceColumn(
                            'Différence reçue en parc palettes',
                            'differenceReceivedInPalletPark',
                            'differenceReceivedInPalletPark'
                        ),
                        getQualityColumn(
                            'Qualité reçue en parc palettes',
                            'qualityReceivedInPalletPark',
                            'qualityReceivedInPalletPark'
                        ),
                    ];
                } else {
                    return [
                        ...baseColumns,
                        getQualityColumn(
                            'Qualité reçue en parc palettes',
                            'QualitéReçueenParcPalettes',
                            'QualitéReçueenParcPalettes'
                        ),
                    ];
                }

            case PackagingFlowDirection.ReturnToSupplier:
                return [...baseColumns, getQualityColumn('Qualitée rendue', 'QualitéeRendue', 'QualitéeRendue')];
            case PackagingFlowDirection.DebtReduction:
                return [
                    getTypologyColumnDebtDeduction('Typologie', 'typology', 'typology'),
                    getQualityColumnDebtDeduction('Qualitée rendue', 'QualitéeRendue', 'QualitéeRendue'),
                ];
            case PackagingFlowDirection.ReceivedFromSupplier:
                return [
                    ...baseColumns,
                    quantityRecievedInStore(
                        'Quantité reçue en magasin',
                        'declaredQuantityByStore',
                        'declaredQuantityByStore'
                    ),
                    getQualityColumn('Qualité reçue en magasin', 'qualityReceivedInStore', 'qualityReceivedInStore'),
                ];
            case PackagingFlowDirection.ReceivedFromSupplierAtOrganization:
                return [
                    ...baseColumns,
                    quantityRecievedInStore(
                        'Quantité reçue en centrale',
                        'declaredQuantityByOrganization',
                        'declaredQuantityByOrganization'
                    ),
                ];
            case PackagingFlowDirection.Increase:
                return [
                    ...baseColumns,
                    quantityRecievedInStore('Quantité ajustée', 'declaredQuantitéajustée', 'declaredQuantitéajustsée'),
                ];
            case PackagingFlowDirection.Decrease:
                return [
                    ...baseColumns,
                    quantityRecievedInStore('Quantité ajustée', 'declaredQuantitéajustée', 'declaredQuantitéajustsée'),
                ];
            case PackagingFlowDirection.ReturnToProvider:
                return [...baseColumns, getQualityColumn('Quantité rendue', 'quantitéRendue', 'quantitéRendue')];
            case PackagingFlowDirection.Transfer:
            case PackagingFlowDirection.ReceptionFromRepair:
            case PackagingFlowDirection.Sale:
            case PackagingFlowDirection.Purchase:
            case PackagingFlowDirection.Destruction:
            case PackagingFlowDirection.ShipmentForRepair:
            case PackagingFlowDirection.ReceiptFromStoreExchange:
                columns = getCommonColumns();
                break;
            default:
                columns = baseColumns;
                break;
        }

        const allPackagesSatisfyCondition = packages.every(
            (pkg: any) =>
                pkg.organizationRefused === null || (pkg.shippedQuantity === pkg.goodCount && pkg.badCount === 0)
        );
        if (differenceReported && !allPackagesSatisfyCondition) {
            columns.push({
                title: 'Statut litige',
                dataIndex: 'status',
                key: 'status',
                render: (_, pkg: any) => {
                    if (
                        pkg.organizationRefused === null ||
                        (pkg.shippedQuantity === pkg.goodCount && pkg.badCount === 0)
                    ) {
                        return <div></div>;
                    }
                    return <div>{pkg.statusDifferenceReported || 'Non vérifié'}</div>;
                },
            });

            if (!storeId) {
                columns.push({
                    title: 'Actions',
                    key: 'actions',
                    dataIndex: 'actions',
                    render: (_, pkg: any) => {
                        const currentStatus = pkg.statusDifferenceReported || 'Non vérifié';
                        const isInvoiced = Boolean(data.invoiceId);
                        const invoicedTooltip =
                            'Une facture a déjà été émise sur ce mouvement, un changement de statut litige n’est plus possible.';
                        const wrapWithTooltip = (node: React.ReactNode) =>
                            isInvoiced ? <Tooltip title={invoicedTooltip}>{node}</Tooltip> : node;
                        return (
                            <div key={pkg._id} style={{ display: 'flex', gap: '8px' }}>
                                {pkg.organizationRefused === null ||
                                (pkg.shippedQuantity === pkg.goodCount && pkg.badCount === 0) ? null : currentStatus ===
                                  'Non vérifié' ? (
                                    <>
                                        {wrapWithTooltip(
                                            <Button
                                                type="primary"
                                                disabled={isInvoiced}
                                                onClick={async () => await acceptPackage(pkg._id)}
                                            >
                                                Accepter
                                            </Button>
                                        )}
                                        {wrapWithTooltip(
                                            <Button
                                                type="primary"
                                                disabled={isInvoiced}
                                                onClick={async () => await refusePackage(pkg._id)}
                                            >
                                                Refuser
                                            </Button>
                                        )}
                                    </>
                                ) : currentStatus === 'Accepté' ? (
                                    wrapWithTooltip(
                                        <Button
                                            type="primary"
                                            disabled={isInvoiced}
                                            onClick={async () => await refusePackage(pkg._id)}
                                        >
                                            Refuser
                                        </Button>
                                    )
                                ) : (
                                    wrapWithTooltip(
                                        <Button
                                            type="primary"
                                            disabled={isInvoiced}
                                            onClick={async () => await acceptPackage(pkg._id)}
                                        >
                                            Accepter
                                        </Button>
                                    )
                                )}
                            </div>
                        );
                    },
                });
            }
        }

        return columns;
    };

    const columns = getColumns(data.flowType, data.differenceReported, storeId);

    const getUpdatedVoucherNumber = useMemo(() => {
        const seenVouchers = new Set<string>();
        return {
            title: "N° bon d'emballage",
            key: 'voucherNumber',
            render: (_: any, record: any) => {
                return useMemo(() => {
                    if (!record) return { children: null, props: { rowSpan: 0 } };

                    const voucherId = record.packagingVoucherId;
                    const voucherNumber = data.packagingVouchers[0]?.voucherNumber || '';

                    if (!seenVouchers.has(voucherId)) {
                        seenVouchers.add(voucherId); // Mark this voucher ID as seen
                        return { children: voucherNumber, props: { rowSpan: rowSpanMap[voucherId] || 0 } };
                    } else {
                        return { children: null, props: { rowSpan: 0 } };
                    }
                }, [record]);
            },
        };
    }, [data.packagingVouchers, rowSpanMap]);

    const getVoucherColumns = () => {
        const baseColumns = [
            {
                title: 'Typologie',
                dataIndex: 'typology',
                key: 'typology',
                render: (_: any, pkg: any) => {
                    return (
                        <>
                            <div key={pkg._id}>
                                {pkg.model && pkg.type
                                    ? pkg.model === pkg.type
                                        ? pkg.type
                                        : `${pkg.type} ${pkg.model}`
                                    : null}
                            </div>
                        </>
                    );
                },
            },
        ];

        return [
            getUpdatedVoucherNumber,
            ...baseColumns,
            getPackagesToBeReturned('Reste à rendre', 'ResteàRendre', 'ResteàRendre'),
        ];
    };
    const voucherColumns = getVoucherColumns();

    // Calculate totals
    const total =
        data.status === 'Expédié vers magasin'
            ? data.packages
                  .filter((pkg: any) => pkg.shippedQuantity > 0)
                  .reduce((sum: number, pkg: any) => {
                      return sum + pkg.shippedQuantity;
                  }, 0)
            : data.packages
                  .filter((pkg: any) => pkg.shippedQuantity > 0 || pkg.goodCount > 0 || pkg.badCount > 0)
                  .reduce((sum: number, pkg: any) => {
                      return sum + (pkg.goodCount + pkg.badCount);
                  }, 0);

    const handleClose = () => {
        refreshDashboard();
        modal.hide();
    };
    return (
        <CustomDrawer
            width={1000}
            title={`${data.sender} _ réception ${data.reciever} _${data.date}`}
            {...props}
            {...antdDrawer(modal)}
            onClose={handleClose}
        >
            <div>
                <div className="mt-4 ">
                    <h3>
                        <b>Informations :</b>
                    </h3>
                    <div className="flex justify-between context">
                        <div className="info-item">
                            <b>Émetteur :</b> {data.sender}
                        </div>
                        <div className="info-item">
                            <b>Destinataire :</b> {data.reciever}
                        </div>
                        <div className="info-item">
                            <b>Statut :</b> {PackagingFlowTypeMap.get(data.flowType)?.label}
                        </div>
                        {(data.userInfo ||
                            data.flowType === PackagingFlowDirection.ReceivedFromSupplierAtOrganization ||
                            data.flowType === PackagingFlowDirection.ShippedToStore) && (
                            <div className="info-item">
                                <b>Utilisateur :</b>
                                {data.userInfo
                                    ? `${data.userInfo.firstName} ${data.userInfo.lastName}`
                                    : data.flowType === PackagingFlowDirection.ReceivedFromSupplierAtOrganization ||
                                      data.flowType === PackagingFlowDirection.ShippedToStore
                                    ? 'Gael'
                                    : ''}
                            </div>
                        )}
                        {data.loadingSession && (
                            <>
                                <div className="info-item">
                                    <b>{formatDate(data.loadingSession.createdAt)}</b>
                                </div>
                                <div className="info-item">
                                    <b>Remorque :</b> {data.loadingSession.trailerNumber}
                                </div>
                                <div className="info-item">
                                    <b>Fournisseur :</b> {data.loadingSession.transporter.name}
                                </div>
                            </>
                        )}
                        {data.cmr && (
                            <div className="info-item">
                                <b>CMR :</b> {data.cmr}
                            </div>
                        )}
                        {data.transportVoucher && (
                            <div className="info-item">
                                <b>Bon de transport :</b> {data.transportVoucher}
                            </div>
                        )}
                        {data.transporter && (
                            <div className="info-item">
                                <b>Fournisseur :</b> {data.transporter.name}
                            </div>
                        )}
                        {data.orderNumber && (
                            <div className="info-item">
                                <b>Numéro commande d&apos;achat :</b> {data.orderNumber}
                            </div>
                        )}
                        {data.flowType === PackagingFlowDirection.ShippedToStore && (
                            <>
                                {data.expeditionAt && moment(data.expeditionAt).isValid() && (
                                    <div className="info-item">
                                        <b>Date expédition :</b> {formatDate(data.expeditionAt)}
                                    </div>
                                )}
                                {data.deliveredAt && moment(data.deliveredAt).isValid() && (
                                    <div className="info-item">
                                        <b>Date livraison :</b> {formatDate(data.deliveredAt)}
                                    </div>
                                )}
                            </>
                        )}
                        {data.truckNumber && (
                            <div className="info-item">
                                <b>Numéro immatriculation :</b> {data.truckNumber}
                            </div>
                        )}
                        {(data.flowType === PackagingFlowDirection.ShippedToStore ||
                            data.flowType === PackagingFlowDirection.ShippedToOrganization) && (
                            <div className="info-item">
                                <b>Flux réceptionné :</b> {data.received ? 'Oui' : 'Non'}
                            </div>
                        )}
                        {data.packagingVouchers.length > 0 &&
                            (data.flowType === PackagingFlowDirection.ReceivedFromSupplier ||
                                data.flowType === PackagingFlowDirection.ReturnToSupplier ||
                                data.flowType === PackagingFlowDirection.ReceivedFromSupplierAtOrganization) && (
                                <div className="info-item">
                                    <b>Statut bon d&apos;emballages :</b>{' '}
                                    {data.packagingVouchers[0]?.expired
                                        ? PackagingVoucherStatus.Expired
                                        : data.packagingVouchers[0]?.flowReturningId === null &&
                                          data.packagingVouchers[0]?.expired === false
                                        ? PackagingVoucherStatus.NotUsed
                                        : PackagingVoucherStatus.Used}
                                </div>
                            )}
                    </div>
                </div>
                <div>
                    <h3 className="mt-16">Détails réception supports et emballages :</h3>
                </div>
                {/* first table */}
                {data.flowType === PackagingFlowDirection.ReturnToSupplier ? (
                    <>
                        {data.packages.some((pkg: any) => pkg.toBeReturned > 0) && (
                            <>
                                <div className="border-bot"></div>
                                <Collapse defaultActiveKey={['1']}>
                                    <Panel header="Reste à rendre" key="1">
                                        <Table
                                            columns={voucherColumns}
                                            dataSource={packages.filter((pkg: any) => pkg.toBeReturned > 0)}
                                            pagination={false}
                                        />
                                    </Panel>
                                </Collapse>
                            </>
                        )}
                        <div className="border-bot"></div>
                        <Collapse
                            defaultActiveKey={!data.packages.some((pkg: any) => pkg.toBeReturned > 0) ? ['1'] : []}
                        >
                            <Panel header="Quantité rendue" key="1">
                                <Table
                                    columns={columns}
                                    dataSource={packages.filter((pkg: any) => pkg.shippedQuantity > 0)}
                                    pagination={false}
                                    footer={() => (
                                        <div className="text-primary">
                                            <b>Total:</b> {total}
                                        </div>
                                    )}
                                />
                            </Panel>
                        </Collapse>
                    </>
                ) : data.flowType === PackagingFlowDirection.DebtReduction &&
                  data.packages.some((pkg: any) => !pkg.rental) ? (
                    <>
                        <div className="border-bot"></div>
                        <Table
                            columns={columns}
                            dataSource={packages.filter((pkg: any) => !pkg.rental && pkg.shippedQuantity > 0)}
                            pagination={false}
                            footer={() => (
                                <div className="text-primary">
                                    <b>Total:</b>{' '}
                                    {data.packages
                                        .filter((pkg: any) => !pkg.rental && pkg.shippedQuantity > 0)
                                        .reduce((sum: number, pkg: any) => sum + (pkg.goodCount + pkg.badCount), 0)}
                                </div>
                            )}
                        />
                    </>
                ) : (
                    <Table
                        columns={columns}
                        dataSource={packages.filter(
                            (pkg: any) => pkg.shippedQuantity > 0 || pkg.goodCount > 0 || pkg.badCount > 0
                        )}
                        pagination={false}
                        footer={() => (
                            <div className="text-primary">
                                <b>Total:</b> {total}
                            </div>
                        )}
                    />
                )}

                {/* Comments Section */}
                <div className="border-bot">
                    <div className="reception-comment">
                        <label htmlFor="comment">Commentaire</label>
                        <TextArea disabled id="comment" rows={4} value={data.comment} className="comment-textarea" />
                    </div>
                </div>
                {/* Supporting Documents Section */}
                {data.flowType === PackagingFlowDirection.ReturnToSupplier ||
                data.flowType === PackagingFlowDirection.DebtReduction ? (
                    <>
                        <h3 className="mt-16">Documents de transport :</h3>
                        <div className="flex context justify-between bg-ultra-light-blue p-16">
                            <div className="text-primary">
                                <div>
                                    <b>Bons d&apos;emballages utilisés :</b>
                                    {Array.isArray(data.packagingVoucherUsed) && data.packagingVoucherUsed.length > 0
                                        ? data.packagingVoucherUsed.map((voucher: any, index: number) => (
                                              <React.Fragment key={voucher._id || index}>
                                                  <span> n°{voucher.voucherNumber || 'N/A'}</span>
                                                  {index < data.packagingVoucherUsed.length - 1 && ', '}
                                              </React.Fragment>
                                          ))
                                        : null}
                                </div>
                                {data.packagingVouchers.length > 0 ? (
                                    <div>
                                        <b>Bon d&apos;emballages :</b>{' '}
                                        {data.packagingVouchers.map((voucher: PackagingVoucher, index: number) => (
                                            <React.Fragment key={index}>
                                                <span>n°{voucher.voucherNumber}</span>
                                                {index < data.packagingVouchers.length - 1 && ', '}
                                            </React.Fragment>
                                        ))}
                                    </div>
                                ) : null}
                            </div>
                            <div>
                                <Button type="primary" onClick={downloadDocuments}>
                                    Télécharger les pièces justificatives
                                </Button>
                                {data.packagingVouchers.length > 0 && (
                                    <Button
                                        type="primary"
                                        className="ml-8"
                                        onClick={async () => await handlePrintVoucher(data.packagingVouchers[0]?._id)}
                                    >
                                        Imprimer le bon
                                    </Button>
                                )}
                            </div>
                        </div>
                    </>
                ) : null}
                {data.attachments.length > 0 &&
                    (data.flowType === PackagingFlowDirection.ShippedToStore ||
                        data.flowType === PackagingFlowDirection.RecivedInStore ||
                        data.flowType === PackagingFlowDirection.RecivedInOrganization ||
                        data.flowType === PackagingFlowDirection.ShippedToOrganization) && (
                        <>
                            <h3 className="mt-16">Documents de transport :</h3>
                            <div className="flex context justify-between bg-ultra-light-blue p-16">
                                <div className="text-primary w-60">
                                    {Array.from(
                                        new Map(
                                            data.attachments.map((attachment: any) => [
                                                attachment.attachmentType,
                                                data.attachments
                                                    .filter(
                                                        (att: any) => att.attachmentType === attachment.attachmentType
                                                    )
                                                    .map((att: any) => att.remoteFile?.originalName || 'N/A'),
                                            ])
                                        ).entries()
                                    ).map(([attachmentType, fileNames], index) => (
                                        <div key={index}>
                                            <b>{attachmentType} :</b> {(fileNames as string[]).join(', ')}
                                        </div>
                                    ))}
                                </div>
                                <div>
                                    <Button type="primary" onClick={downloadFiles}>
                                        Télécharger les pièces justificatives
                                    </Button>
                                </div>
                            </div>
                        </>
                    )}
                {data.packagingVouchers.length > 0 &&
                (data.flowType === PackagingFlowDirection.ReceivedFromSupplier ||
                    data.flowType === PackagingFlowDirection.ReceivedFromSupplierAtOrganization) ? (
                    <>
                        <h3 className="mt-16">Documents de transport :</h3>
                        <div className="flex context justify-between bg-ultra-light-blue p-16">
                            <div className="text-primary w-50">
                                <>
                                    <b>Bon d&apos;emballages :</b>{' '}
                                    {data.packagingVouchers.map((voucher: PackagingVoucher, index: number) => (
                                        <React.Fragment key={index}>
                                            <span>n°{voucher.voucherNumber}</span>
                                            {index < data.packagingVouchers.length - 1 && ', '}
                                        </React.Fragment>
                                    ))}
                                    {Array.from(
                                        new Map(
                                            data.attachments.map((attachment: any) => [
                                                attachment.attachmentType,
                                                data.attachments
                                                    .filter(
                                                        (att: any) => att.attachmentType === attachment.attachmentType
                                                    )
                                                    .map((att: any) => att.remoteFile?.originalName || 'N/A'),
                                            ])
                                        ).entries()
                                    ).map(([attachmentType, fileNames], index) => (
                                        <div key={index}>
                                            <b>{attachmentType} :</b> {(fileNames as string[]).join(', ')}
                                        </div>
                                    ))}
                                </>
                            </div>
                            <div>
                                <Button type="primary" onClick={downloadDocuments}>
                                    Télécharger les pièces justificatives
                                </Button>
                                <Button
                                    type="primary"
                                    className="ml-8"
                                    onClick={async () => await handlePrintVoucher(data.packagingVouchers[0]?._id)}
                                >
                                    Imprimer le bon
                                </Button>
                            </div>
                        </div>
                    </>
                ) : null}
                {/* Retour Button Section */}
                <div className="flex justify-start mt-4">
                    <Button type="primary" className="mt-16" onClick={handleClose}>
                        Retour
                    </Button>
                </div>
            </div>
        </CustomDrawer>
    );
});

export default TableDetailsFormDrawer;
